import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

// Carpeta por defecto (personal interno). Los productores suben a una carpeta
// propia (`instrucciones/productores/{uid}`) porque las reglas de Storage solo
// les permiten tocar sus propios archivos.
export const DEFAULT_ATTACHMENT_FOLDER = 'instrucciones';
export const getProductorAttachmentFolder = (uid) => `instrucciones/productores/${uid}`;

export const ALLOWED_ATTACHMENT_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
];

export const ALLOWED_ATTACHMENT_ACCEPT =
    '.png,.jpg,.jpeg,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,image/*,application/pdf';

const sanitizeFileName = (name) => {
    return (name || 'archivo')
        .normalize('NFKD')
        .replace(/[^a-zA-Z0-9._-]+/g, '_')
        .replace(/_+/g, '_')
        .slice(0, 120);
};

// --- Portapapeles (Ctrl+V) ---------------------------------------------------
// El navegador entrega las capturas de pantalla como un File genérico
// ("image.png"), así que el nombre visible del adjunto lo armamos nosotros.
const CLIPBOARD_EXTENSION_BY_TYPE = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
};

const hasGenericClipboardName = (name) => !name || /^image\.[a-z0-9]+$/i.test(name);

const pad2 = (value) => String(value).padStart(2, '0');

export const buildPastedFileName = (type, date = new Date(), index = 0) => {
    const extension = CLIPBOARD_EXTENSION_BY_TYPE[type];
    const stamp = `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`
        + `-${pad2(date.getHours())}${pad2(date.getMinutes())}${pad2(date.getSeconds())}`;
    const suffix = index > 0 ? `-${index + 1}` : '';
    return `captura-${stamp}${suffix}.${extension}`;
};

/**
 * Archivos adjuntables que trae un evento de pegado: capturas de pantalla,
 * imágenes copiadas de una página y archivos copiados del explorador.
 * Devuelve [] si el portapapeles solo trae texto.
 */
export const getClipboardAttachments = (clipboardData, date = new Date()) => {
    if (!clipboardData) return [];
    // `files` cubre el caso habitual; algunos navegadores solo exponen `items`.
    const files = Array.from(clipboardData.files || []);
    const candidates = files.length
        ? files
        : Array.from(clipboardData.items || [])
            .filter(item => item.kind === 'file')
            .map(item => item.getAsFile())
            .filter(Boolean);

    return candidates.map((file, index) => {
        const needsName = hasGenericClipboardName(file.name) && CLIPBOARD_EXTENSION_BY_TYPE[file.type];
        if (!needsName) return file;
        return new File([file], buildPastedFileName(file.type, date, index), {
            type: file.type,
            lastModified: file.lastModified,
        });
    });
};

export const isAttachmentTypeAllowed = (file) => {
    if (!file) return false;
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) return false;
    if (!file.type) return true;
    return ALLOWED_ATTACHMENT_TYPES.includes(file.type);
};

export const uploadInstruccionAttachment = async (file, { uploadedById, uploadedByName, folder } = {}) => {
    const safeName = sanitizeFileName(file.name);
    const uniqueId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const baseFolder = String(folder || DEFAULT_ATTACHMENT_FOLDER).replace(/\/+$/, '');
    const path = `${baseFolder}/${uniqueId}_${safeName}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file, { contentType: file.type || 'application/octet-stream' });
    const url = await getDownloadURL(fileRef);
    return {
        name: file.name,
        path,
        url,
        type: file.type || '',
        size: file.size || 0,
        uploadedAt: new Date().toISOString(),
        uploadedById: uploadedById || '',
        uploadedByName: uploadedByName || '',
    };
};

export const deleteInstruccionAttachment = async (path) => {
    if (!path) return;
    try {
        await deleteObject(ref(storage, path));
    } catch (error) {
        if (error?.code !== 'storage/object-not-found') {
            console.error('Error eliminando adjunto:', error);
        }
    }
};

export const formatAttachmentSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
